import { TeamRole } from '@anm/api/modules/team';
import FormHelperText from '@anm/components/form/FormHelperText';
import useSwitchState from '@anm/hooks/useSwitchState';
import { FC } from 'react';
import { FieldRenderProps } from 'react-final-form';
import { UserTeamPermission } from 'types/user';

import DenyRoleChange from './DenyRoleChange';
import OwnerAlert from './OwnerAlert';
import RoleRadioBlock from './RoleRadioBlock';
import TeamMemberRolesInputWrapper from './Wrapper';

export type TeamMemberRolesInputProps = FieldRenderProps<TeamRole, HTMLDivElement> & {
  currentRole: TeamRole;
  nextOwnerName: string;
  label?: string;
  permissions?: UserTeamPermission;
  isVisibleOwnerRole?: boolean;
};

const TeamMemberRolesInput: FC<TeamMemberRolesInputProps> = ({
  input: { value, onChange },
  meta: { error, touched },
  permissions = {},
  nextOwnerName,
  currentRole,
  isVisibleOwnerRole = true
}) => {
  const [isOwnerAlert, openOwnerAlert, closeOwnerAlert] = useSwitchState();
  const { canChangeOwner, canGrantAdmin, canGrantNonAdmin } = permissions;

  const handleChange = (value: TeamRole) => {
    value === 'OWNER' && openOwnerAlert();
    onChange(value);
  };

  return (
    <>
      {isOwnerAlert && (
        <OwnerAlert nextOwnerName={nextOwnerName} onClose={closeOwnerAlert} onMakeOwner={closeOwnerAlert} />
      )}
      <TeamMemberRolesInputWrapper>
        {touched && <FormHelperText variant="static">{error}</FormHelperText>}
        {currentRole === 'OWNER' && <DenyRoleChange />}
        {canGrantNonAdmin && (
          <>
            <RoleRadioBlock
              features={['Can see team projects and broadcasts']}
              isChecked={value === 'VIEWER'}
              label="Viewer"
              value="VIEWER"
              onChange={onChange}
            />
            <RoleRadioBlock
              features={['Can create, delete and edit his own projects and broadcasts']}
              isChecked={value === 'EDITOR'}
              label="Editor"
              value="EDITOR"
              onChange={onChange}
            />
          </>
        )}
        {canGrantAdmin && (
          <RoleRadioBlock
            features={[
              'Can see, invite and remove team members',
              'Can assign roles for editors and viewers',
              'Can create, delete and edit all team projects and broadcasts'
            ]}
            isChecked={value === 'ADMIN'}
            label="Admin"
            value="ADMIN"
            onChange={onChange}
          />
        )}
        {isVisibleOwnerRole && canChangeOwner && (
          <RoleRadioBlock
            features={[
              'Can see, invite and remove team members',
              'Can manage team subscription and payment details',
              'Can assign roles for all team members ',
              'Can create, delete and edit all team projects and broadcasts'
            ]}
            isChecked={value === 'OWNER'}
            label="Owner"
            value="OWNER"
            onChange={handleChange}
          />
        )}
      </TeamMemberRolesInputWrapper>
    </>
  );
};
export default TeamMemberRolesInput;
